import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Tabs } from 'src/common';
import SimpleBreadCrumb from 'src/components/widgets/BreadCrumb/SimpleBreadCrumb';
import { getPageTitle } from 'src/config/projectConfig';
import {
  getSecurityEndpointDetailsHref,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { getEndpointPreviewRow } from '../../preview/endpoints';
import { SubscriptionUnavailableMessage } from '../../shared/edrPrimitives';
import EndpointAutomatedResponseTab from './EndpointAutomatedResponseTab';
import EndpointComplianceTab from './EndpointComplianceTab';
import EndpointDashboardTab from './EndpointDashboardTab';
import EndpointFileMonitoringTab from './EndpointFileMonitoringTab';
import EndpointMalwareTab from './EndpointMalwareTab';
import EndpointMitreTab from './EndpointMitreTab';
import EndpointThreatHuntingTab from './EndpointThreatHuntingTab';
import EndpointTechnicalDetailsTab from './EndpointTechnicalDetailsTab';
import EndpointVulnerabilitiesTab from './EndpointVulnerabilitiesTab';
import {
  DEFAULT_ENDPOINT_DETAILS_TAB,
  ENDPOINT_DETAILS_TABS,
  isEndpointDetailsTab,
  type EndpointDetailsTab,
} from './endpointDetailsTabs';

type EndpointDetailsLocationState = {
  endpointName?: string;
};

/** Tab is visible only when at least one registered widget is allowed. */
const hasAnyWidget = (
  canWidget: (componentKey: string) => boolean,
  componentKeys: readonly string[],
): boolean => componentKeys.some((key) => canWidget(key));

const EndpointDetails: FC = () => {
  const { systemWazuhAgentId: encodedSystemWazuhAgentId = '', tab: tabParam } =
    useParams<{ systemWazuhAgentId: string; tab?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  /** Dashboard + technical details keep endpoint-specific modules; other tabs reuse EDR sidebar modules. */
  const { canWidget: canDashboardWidget, permissionsResolved } =
    useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'endpoint-dashboard');
  const { canWidget: canVulnerabilitiesWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'vulnerability-detection',
  );
  const { canWidget: canMalwareWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'malware-detection',
  );
  const { canWidget: canMitreWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'mitre-attck',
  );
  const { canWidget: canFileMonitoringWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'file-integrity-monitoring',
  );
  const { canWidget: canThreatHuntingWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'threat-hunting',
  );
  const { canWidget: canAutomatedResponseWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'automated-response',
  );
  const { canWidget: canComplianceWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'configuration-compliance',
  );
  const { canWidget: canTechnicalDetailsWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'endpoint-technical-details',
  );

  const canDashboard = hasAnyWidget(canDashboardWidget, [
    'portfolioHealth',
    'securityScore',
    'criticalVulnerabilities',
    'failedCompliance',
    'securityOverview',
    'endpointSummary',
    'endpointHealth',
  ]);
  const canVulnerabilities = hasAnyWidget(canVulnerabilitiesWidget, ['summary', 'list']);
  const canMalware = hasAnyWidget(canMalwareWidget, ['summary', 'list']);
  const canMitre = hasAnyWidget(canMitreWidget, ['summary', 'techniquesList']);
  const canFileMonitoring = hasAnyWidget(canFileMonitoringWidget, ['summary', 'events']);
  const canThreatHunting = hasAnyWidget(canThreatHuntingWidget, ['summary', 'resultsList']);
  const canAutomatedResponse = hasAnyWidget(canAutomatedResponseWidget, ['summary', 'list']);
  const canCompliance = hasAnyWidget(canComplianceWidget, ['summary', 'list']);
  const canTechnicalDetails = hasAnyWidget(canTechnicalDetailsWidget, ['installCommands']);

  const visibleTabs = useMemo(() => {
    if (!permissionsResolved) return [];
    const tabAccess: Record<EndpointDetailsTab, boolean> = {
      dashboard: canDashboard,
      vulnerabilities: canVulnerabilities,
      'malware-detection': canMalware,
      'mitre-attck': canMitre,
      'file-monitoring': canFileMonitoring,
      'threat-hunting': canThreatHunting,
      'automated-response': canAutomatedResponse,
      compliance: canCompliance,
      'technical-details': canTechnicalDetails,
    };
    return ENDPOINT_DETAILS_TABS.filter((tab) => tabAccess[tab.value]);
  }, [
    permissionsResolved,
    canDashboard,
    canVulnerabilities,
    canMalware,
    canMitre,
    canFileMonitoring,
    canThreatHunting,
    canAutomatedResponse,
    canCompliance,
    canTechnicalDetails,
  ]);

  const systemWazuhAgentId = decodeURIComponent(encodedSystemWazuhAgentId).trim();
  const endpointName = (location.state as EndpointDetailsLocationState | null)?.endpointName?.trim();
  const previewRow =
    !wait && isPreviewMode ? getEndpointPreviewRow(systemWazuhAgentId) : null;
  const displayName =
    endpointName || previewRow?.endpoint || previewRow?.name || systemWazuhAgentId;

  const requestedTab = isEndpointDetailsTab(tabParam) ? tabParam : null;
  const activeVisibleTab = useMemo((): EndpointDetailsTab | null => {
    if (!visibleTabs.length) return null;
    if (requestedTab && visibleTabs.some((tab) => tab.value === requestedTab)) {
      return requestedTab;
    }
    return visibleTabs[0]?.value ?? DEFAULT_ENDPOINT_DETAILS_TAB;
  }, [requestedTab, visibleTabs]);

  // Keep URL in sync: missing/invalid/unauthorized tab → first visible (or dashboard).
  useEffect(() => {
    if (!permissionsResolved || !systemWazuhAgentId || !activeVisibleTab) return;
    if (tabParam === activeVisibleTab) return;
    navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId, activeVisibleTab), {
      replace: true,
      state: location.state,
    });
  }, [
    permissionsResolved,
    systemWazuhAgentId,
    activeVisibleTab,
    tabParam,
    navigate,
    location.state,
  ]);

  const handleTabChange = (_: unknown, nextTab: string) => {
    if (!isEndpointDetailsTab(nextTab) || !systemWazuhAgentId) return;
    navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId, nextTab), {
      state: location.state,
    });
  };

  const breadcrumbData = {
    links: [
      { name: 'Endpoints', to: SECURITY_ENDPOINTS_HREF },
      {
        name: displayName,
        to: systemWazuhAgentId
          ? getSecurityEndpointDetailsHref(systemWazuhAgentId, DEFAULT_ENDPOINT_DETAILS_TAB)
          : '',
      },
    ],
  };

  if (!permissionsResolved) return null;

  return (
    <>
      <Helmet>
        <title>{getPageTitle(`Security — ${displayName}`)}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{
          minHeight: '100%',
          boxSizing: 'border-box',
          gap: 16,
          minWidth: 0,
        }}
      >
        <SimpleBreadCrumb data={breadcrumbData} />

        <Box display="flex" flexDirection="column" style={{ gap: 8, minWidth: 0 }}>
          <Typography
            component="h1"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 22,
              fontWeight: 700,
              color: '#1E293B',
              flexShrink: 0,
            }}
          >
            {displayName}
          </Typography>

          {visibleTabs.length > 0 && activeVisibleTab ? (
            <Box
              style={{
                borderBottom: '1px solid #E2E8F0',
                minWidth: 0,
              }}
            >
              <Tabs
                activeTab={activeVisibleTab}
                handleTabChange={handleTabChange}
                tabs={visibleTabs}
                isTransform={false}
                scrollable
                noBottomMargin
              />
            </Box>
          ) : null}
        </Box>

        {visibleTabs.length === 0 ? (
          <SubscriptionUnavailableMessage
            messageKey="unavailable.endpointDetails"
            padded
          />
        ) : null}

        {activeVisibleTab === 'dashboard' && (
          <EndpointDashboardTab
            systemWazuhAgentId={systemWazuhAgentId}
            displayName={displayName}
          />
        )}
        {activeVisibleTab === 'vulnerabilities' && (
          <EndpointVulnerabilitiesTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'malware-detection' && (
          <EndpointMalwareTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'mitre-attck' && (
          <EndpointMitreTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'file-monitoring' && (
          <EndpointFileMonitoringTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'threat-hunting' && (
          <EndpointThreatHuntingTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'automated-response' && (
          <EndpointAutomatedResponseTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'compliance' && (
          <EndpointComplianceTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeVisibleTab === 'technical-details' && (
          <EndpointTechnicalDetailsTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
      </Box>
    </>
  );
};

export default EndpointDetails;
